import type { PropsWithChildren } from "react";
import { Box, Button, Flex, Icon, Text, type As } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import type { Stat } from "components/stats-grid";
import { generateSingularLabel } from "utils/generate-singular-label";

const StatItemWrapper = ({
  navigateTo,
  children,
}: PropsWithChildren<{ navigateTo: Stat["navigateTo"] }>) => {
  const child = <Box p={1}>{children}</Box>;
  if (!navigateTo) return child;

  const { to, navigateOptions } = navigateTo;

  return (
    <Button
      variant={"ghost"}
      display={"block"}
      p={0}
      h={"100%"}
      as={Link}
      to={to}
      {...navigateOptions}
      _hover={{
        boxShadow: "base",
        bg: "indigo.100",
      }}
    >
      {child}
    </Button>
  );
};

type StatsCardProps = {
  stat: Stat;
  icon?: As;
  iconColor?: string;
  iconBgColor?: string;
  prefix?: string;
};

export function StatsCard({
  stat,
  icon,
  iconColor = "client.accent.text",
  iconBgColor = "client.accent.background",
  prefix = "",
}: StatsCardProps) {
  return (
    <StatItemWrapper navigateTo={stat.navigateTo}>
      <Flex
        direction={"row"}
        align={"flex-start"}
        justify={"flex-start"}
        h={"100%"}
        columnGap={"12px"}
      >
        {icon ? (
          <Box
            minW={{ base: "32px", sm: "40px" }}
            minH={{ base: "32px", sm: "40px" }}
            borderRadius={"8px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            bg={iconBgColor}
          >
            <Icon as={icon} boxSize={"16px"} color={iconColor} />
          </Box>
        ) : null}
        <Flex direction={"column"}>
          <Text fontSize={"2xl"} fontWeight={700} lineHeight={"28px"}>
            {prefix}
            {stat.value?.toLocaleString?.()}
          </Text>
          <Text
            fontSize={"sm"}
            color={"gray.600"}
            fontWeight={400}
            lineHeight={"16px"}
          >
            {generateSingularLabel(stat.label, Number(stat.value) || 0)}
          </Text>
        </Flex>
      </Flex>
    </StatItemWrapper>
  );
}
